/**
 * INTELLIGENCE STORE
 * Shared data layer that accumulates insights across features.
 * Uses localStorage for persistence + React Context for reactivity.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const IntelligenceContext = createContext(null);

const STORAGE_KEY = 'isupplyai_intelligence';

const getStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const persist = (data) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
};

export const IntelligenceProvider = ({ children }) => {
  const [store, setStore] = useState(getStored);

  useEffect(() => { persist(store); }, [store]);

  const storeTrendingTopics = useCallback((topics) => {
    setStore(prev => ({ ...prev, trendingTopics: topics, trendingTimestamp: Date.now() }));
  }, []);

  const storeCompetitorData = useCallback((data) => {
    setStore(prev => ({
      ...prev,
      competitorGaps: data.gaps || prev.competitorGaps || [],
      competitorContent: data.content || prev.competitorContent || [],
      competitorTimestamp: Date.now(),
    }));
  }, []);

  const storeGeneratedContent = useCallback((topic) => {
    setStore(prev => ({
      ...prev,
      generatedTopics: [...(prev.generatedTopics || []).slice(-20), { topic, timestamp: Date.now() }],
    }));
  }, []);

  const getIntelligenceSummary = useCallback(() => {
    const now = Date.now();
    const oneDay = 86400000;
    const oneWeek = oneDay * 7;
    return {
      trendingCount: (store.trendingTopics || []).length,
      gapsCount: (store.competitorGaps || []).length,
      generatedThisWeek: (store.generatedTopics || []).filter(t => now - t.timestamp < oneWeek).length,
      hasTrendingData: !!store.trendingTimestamp && (now - store.trendingTimestamp < oneDay),
      hasCompetitorData: !!store.competitorTimestamp && (now - store.competitorTimestamp < oneDay * 7),
      suggestedTopics: (store.trendingTopics || []).slice(0, 3).map(t => t.topic || t),
      uncoveredGaps: (store.competitorGaps || []).filter(g => !g.covered).slice(0, 5),
      coveredTopics: (store.generatedTopics || []).map(t => t.topic?.toLowerCase()),
    };
  }, [store]);

  // Check if a topic is already covered by user's generated content
  const isTopicCovered = useCallback((topic) => {
    const covered = (store.generatedTopics || []).map(t => t.topic?.toLowerCase());
    return covered.some(c => c && topic?.toLowerCase().includes(c));
  }, [store]);

  // Check if a trending topic matches an Athena gap
  const isUncoveredGap = useCallback((topicText) => {
    const gaps = store.competitorGaps || [];
    if (gaps.length === 0) return false;
    const lower = topicText?.toLowerCase() || '';
    return gaps.some(g => {
      const gapText = (g.topic || g.gap || g.text || '').toLowerCase();
      return gapText && (lower.includes(gapText) || gapText.includes(lower));
    });
  }, [store]);

  return (
    <IntelligenceContext.Provider value={{
      store, storeTrendingTopics, storeCompetitorData, storeGeneratedContent,
      getIntelligenceSummary, isTopicCovered, isUncoveredGap,
    }}>
      {children}
    </IntelligenceContext.Provider>
  );
};

export const useIntelligence = () => {
  const ctx = useContext(IntelligenceContext);
  if (!ctx) throw new Error('useIntelligence must be used within IntelligenceProvider');
  return ctx;
};

export default IntelligenceContext;
